import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Row, Col, Select, Checkbox, Upload, Space, Spin, Tag, App } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, InboxOutlined, FolderOutlined, DownloadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../components/Layout/MainLayout';
import SmartEditor from '../../components/Common/SmartEditor';
import { draftService } from '../../services/draftService';

const { Dragger } = Upload;

const DraftTmpView: React.FC = () => {
  const { idx } = useParams<{ idx: string }>();
  const navigate = useNavigate();
  const { modal } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [sendMail, setSendMail] = useState(true);
  const [sendSMS, setSendSMS] = useState(true);
  const [draftNo, setDraftNo] = useState('');
  const [receivers, setReceivers] = useState<any[]>([]);
  const [existingFiles, setExistingFiles] = useState<string[]>([]);
  const [existingFileReNames, setExistingFileReNames] = useState<string[]>([]);

  // 파일 다운로드
  const handleDownloadFile = (fileName: string) => {
    window.open(`/download/${fileName}`, '_blank');
  };

  // 임시 기안서 데이터 로드
  useEffect(() => {
    if (idx) {
      fetchTmpData(parseInt(idx));
    }
  }, [idx]);

  const fetchTmpData = async (tmpIdx: number) => {
    setDataLoading(true);
    try {
      // 임시 기안서 조회 및 초기 데이터 조회
      const [tmpResponse, initResponse] = await Promise.all([
        draftService.getTmpDetail(tmpIdx),
        draftService.getDraftInit()
      ]);

      console.log('📥 임시 기안서 데이터:', tmpResponse.data);

      // 초기 데이터 설정 (기안번호, 수신자)
      if (initResponse.resultCode === 200) {
        setDraftNo(initResponse.data.draftNo);
        setReceivers(initResponse.data.receivers || []);
      }

      // 임시 데이터 설정
      if (tmpResponse.resultCode === 200) {
        const tmpData = tmpResponse.data;

        // 기존 첨부파일 설정
        if (tmpData.File_Names && tmpData.File_Names.trim() !== '') {
          setExistingFiles(tmpData.File_Names.split(', ').filter((f: string) => f.trim() !== ''));
        }
        if (tmpData.File_ReNames && tmpData.File_ReNames.trim() !== '') {
          setExistingFileReNames(tmpData.File_ReNames.split(', ').filter((f: string) => f.trim() !== ''));
        }

        form.setFieldsValue({
          Draft_Subject: tmpData.Draft_Subject,
          Draft_Cost: tmpData.Draft_Cost,
          Draft_Client: tmpData.Draft_Client,
        });

        // HTML 엔티티 디코딩
        const decodeHTML = (html: string) => {
          const txt = document.createElement('textarea');
          txt.innerHTML = html;
          return txt.value;
        };

        // 스마트에디터에 내용 설정
        const setEditorContent = () => {
          if ((window as any).oEditors && (window as any).oEditors[0]) {
            // 먼저 에디터 내용 초기화
            (window as any).oEditors[0].exec('SET_IR', ['']);

            if (tmpData.Draft_Contents) {
              const decodedContent = decodeHTML(tmpData.Draft_Contents);
              // 내용 설정
              setTimeout(() => {
                (window as any).oEditors[0].exec('SET_IR', [decodedContent]);
                console.log('✅ 임시 기안서 내용 로드 완료');
              }, 300);
            }
          } else {
            setTimeout(setEditorContent, 500);
          }
        };

        setTimeout(setEditorContent, 2000);
      }

    } catch (error) {
      console.error('임시 기안서 조회 오류:', error);
      message.error('임시 기안서를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setDataLoading(false);
    }
  };

  const handleSubmit = (values: any) => {
    modal.confirm({
      title: '기안서 등록',
      content: '저장 하시겠습니까?\n저장 후에 기안서 리스트 화면으로 돌아갑니다.',
      okText: '저장',
      cancelText: '취소',
      onOk: async () => {
        setLoading(true);
        try {
          // 스마트에디터 내용 추출
          const content = (window as any).getContent_smarteditor?.();

          const formData = new FormData();
          formData.append('Draft_No', draftNo);
          formData.append('Draft_Subject', values.Draft_Subject);
          formData.append('Draft_Cost', values.Draft_Cost);
          formData.append('Draft_Client', values.Draft_Client);
          formData.append('Receiver', values.Receiver || '');
          formData.append('editorTxt', content || '');
          formData.append('Send_Mail_YN', sendMail ? 'Y' : 'N');
          formData.append('Send_SMS_YN', sendSMS ? 'Y' : 'N');
          formData.append('Draft_Tmp_IDX', idx!);  // 임시저장 ID 전달

          // 파일 추가
          fileList.forEach((file) => {
            if (file.originFileObj) {
              formData.append('fileInput', file.originFileObj);
            }
          });

          console.log('임시 기안서 정식 등록:', { ...values, content, fileCount: fileList.length });

          // 정식 기안서 등록 API 호출 (draftService 사용)
          const result = await draftService.createDraft(formData);

          if (result.resultCode === 200) {
            message.success('기안서가 등록되었습니다.');

            // 임시저장 삭제 (정식 등록되었으므로)
            if (idx) {
              await draftService.deleteTmp(parseInt(idx));
            }

            navigate('/draft/list');
          } else {
            message.error(result.message || '기안서 등록 실패');
          }
        } catch (error) {
          console.error('기안서 등록 오류:', error);
          message.error('기안서 등록 중 오류가 발생했습니다.');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleTempSave = () => {
    modal.confirm({
      title: '임시저장',
      content: '임시저장 하시겠습니까?\n저장 후에 임시 저장 리스트 화면으로 돌아갑니다.',
      okText: '저장',
      cancelText: '취소',
      onOk: async () => {
        try {
          setLoading(true);

          // 폼 값 가져오기 (검증 없이)
          const values = form.getFieldsValue();

          // 스마트에디터 내용 추출
          const content = (window as any).getContent_smarteditor?.();

          // 필수 필드 체크 (제목, 비용, 지출처만)
          if (!values.Draft_Subject || !values.Draft_Cost || !values.Draft_Client) {
            message.warning('제목, 비용, 지출처는 필수 입력 항목입니다.');
            return;
          }

          // draftService 사용 (세션 쿠키 자동 전송)
          const result = await draftService.updateTmp(parseInt(idx!), {
            Draft_Subject: values.Draft_Subject,
            Draft_Contents: content || '',
            Draft_Cost: values.Draft_Cost,
            Draft_Client: values.Draft_Client
          });

          if (result.resultCode === 200) {
            message.success('임시저장이 업데이트되었습니다.');
            navigate('/draft/tmp-list');
          } else {
            message.error(result.message || '임시저장 업데이트 실패');
          }
        } catch (error) {
          console.error('임시저장 업데이트 오류:', error);
          message.error('임시저장 업데이트 중 오류가 발생했습니다.');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const uploadProps = {
    multiple: true,
    fileList,
    beforeUpload: (file: any) => {
      const uploadFile: UploadFile = {
        uid: `${Date.now()}-${file.name}`,
        name: file.name,
        status: 'done',
        originFileObj: file,
      };
      setFileList([...fileList, uploadFile]);
      return false;
    },
    onRemove: (file: UploadFile) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
  };

  if (dataLoading) {
    return (
      <MainLayout>
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Spin size="large" tip="임시 기안서를 불러오는 중..." />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Card
        title="임시 기안서 불러오기"
        extra={
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/draft/tmp-list')}>
            목록으로
          </Button>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            Draft_Cost: 0,
            Send_Mail_YN: true,
            Send_SMS_YN: true,
          }}
        >
          {/* 1줄: 기안번호, 수신자, 메일/SMS, 비용, 지출처 */}
          <Row gutter={16}>
            <Col span={3}>
              <Form.Item label="기안번호">
                <Input disabled value={draftNo} />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                label="수신(결재자)"
                name="Receiver"
                rules={[{ required: true, message: '수신자 선택' }]}
              >
                <Select placeholder="선택">
                  {receivers.map((r) => (
                    <Select.Option
                      key={r.Admin_Name}
                      value={`${r.Admin_Name} ${r.Admin_Position}|${r.Admin_Email}|${r.Admin_Cell}`}
                    >
                      {r.Admin_Name} {r.Admin_Position}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="발송 옵션">
                <Space>
                  <Checkbox checked={sendMail} onChange={(e) => setSendMail(e.target.checked)}>
                    메일
                  </Checkbox>
                  <Checkbox checked={sendSMS} onChange={(e) => setSendSMS(e.target.checked)}>
                    SMS
                  </Checkbox>
                </Space>
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                label="비용"
                name="Draft_Cost"
                rules={[{ required: true, message: '금액 필수' }]}
              >
                <Input type="number" placeholder="금액" addonAfter="원" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item label="지출처" name="Draft_Client">
                <Input placeholder="지출처" />
              </Form.Item>
            </Col>
          </Row>

          {/* 2줄: 제목 */}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="제목"
                name="Draft_Subject"
                rules={[{ required: true, message: '제목을 입력해주세요' }]}
              >
                <Input placeholder="제목을 입력하세요" />
              </Form.Item>
            </Col>
          </Row>

          {/* 기존 첨부파일 */}
          {existingFiles.length > 0 && (
            <Form.Item label="기존 첨부파일">
              <div style={{
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                padding: '12px',
                background: '#fafafa'
              }}>
                {existingFiles.map((file, index) => (
                  <div key={index} style={{
                    padding: '8px 12px',
                    marginBottom: index < existingFiles.length - 1 ? 8 : 0,
                    background: '#fff',
                    borderRadius: '4px',
                    border: '1px solid #e8e8e8',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span>📎 {file}</span>
                    <Space>
                      <Tag color="blue">임시저장</Tag>
                      <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownloadFile(existingFileReNames[index] || file)}
                      >
                        다운로드
                      </Button>
                    </Space>
                  </div>
                ))}
              </div>
            </Form.Item>
          )}

          {/* 새 첨부파일 추가 */}
          <Form.Item label="새 첨부파일 추가">
            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">클릭하거나 파일을 드래그하여 업로드</p>
              <p className="ant-upload-hint">
                새로운 파일을 추가할 수 있습니다.
              </p>
            </Dragger>
          </Form.Item>

          {/* 스마트에디터 */}
          <Form.Item label="내용">
            <SmartEditor id="smarteditor" height="400px" />
          </Form.Item>

          {/* 버튼 */}
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={handleTempSave} icon={<FolderOutlined />}>
                임시저장
              </Button>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
                작성 완료
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Card>
    </MainLayout>
  );
};

export default DraftTmpView;
